import toColorString from 'polished/lib/color/toColorString';
import PropTypes from 'prop-types';
import rem from 'polished/lib/helpers/rem';
import React, { useCallback, useMemo, useState } from 'react';
import find from 'lodash/find';

import { t } from '@lingui/macro';

import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';

import Avatar from '@decisiv/ui-components/lib/components/Avatar';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Menu from '@decisiv/ui-components/lib/components/Menu';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import KDSTopNav from '@decisiv/ui-components/lib/components/TopNav';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { bffFlipperLink, logoutURL } from 'bff';

import ROUTE_NAMES from 'setup/Routing/routeNames';

import { useCurrentUserContext } from 'utils/User';
import useSentinelTeam from 'hooks/useSentinelTeam';
import useUserMenuInfoQuery from './AppsLinks/useUserMenuInfoQuery';

function UserMenu(props) {
  /* istanbul ignore next -- @preserve */
  const { variant = 'full' } = props;
  const {
    email,
    needsSentinelTeam,
    name,
    roleLabel,
    sentinelTeams,
    isSuperUser,
  } = useCurrentUserContext();
  const { belongsToOemSegment, ...sentinelTeam } = useSentinelTeam();

  const [popover, setPopover] = useState({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const popoverRef = useCallback(setPopover, [popover]);

  const isSentinelTeamMember = useMemo(
    () => find(sentinelTeams, (sg) => sg.id === sentinelTeam?.id),
    [sentinelTeam, sentinelTeams],
  );

  const canViewNotification = sentinelTeam && isSentinelTeamMember;
  const canFetchMyProfileLink = belongsToOemSegment && !needsSentinelTeam;

  const { data: caseLinksData } = useUserMenuInfoQuery({
    skip: !canFetchMyProfileLink,
  });

  const caseLink =
    caseLinksData?.currentUserProfile?.user?.portalUrls?.[0]?.url;

  const menuItems = useMemo(() => {
    const items = [
      {
        id: 'logout',
        text: t`Log Out`,
        linkTo: logoutURL(),
      },
    ];

    if (isSuperUser) {
      items.unshift({
        id: 'feature-flag',
        text: t`Feature Flags`,
        linkTo: '#',
        onSelect: () => {
          window.open(bffFlipperLink, '_blank', 'noopener,noreferrer');
          popover.hide();
        },
      });
    }

    /* istanbul ignore next -- @preserve */
    if (!needsSentinelTeam) {
      if (canViewNotification) {
        items.unshift({
          id: 'notification-preferences',
          text: t`Notification Preferences`,
          linkTo: ROUTE_NAMES.notification_preferences,
          onSelect: () => {
            popover.hide();
          },
        });
      }

      if (isSuperUser) {
        items.unshift({
          id: 'all-teams',
          text: t`Back to All Teams`,
          onSelect: () => {
            popover.hide();
          },
          linkTo: ROUTE_NAMES.teams,
        });
      }
    }

    if (belongsToOemSegment && Boolean(caseLink)) {
      items.unshift({
        id: 'case-profile',
        text: t`My Profile`,
        linkTo: `#`,
        onSelect: () => {
          window.open(`${caseLink}/my_profile`, '_blank');
          popover.hide();
        },
      });
    }

    return items;
  }, [
    caseLink,
    belongsToOemSegment,
    canViewNotification,
    isSuperUser,
    needsSentinelTeam,
    popover,
  ]);

  const emblem = (emblemProps) => <Avatar {...emblemProps} email={email} />;
  const primaryText = name || email;
  return (
    <KDSTopNav.Menu
      emblem={emblem}
      primaryText={
        variant !== 'compact'
          ? primaryText
          : /* istanbul ignore next -- @preserve */ undefined
      }
      popoverRef={popoverRef}
      secondaryText={undefined}
      zIndex={10}
    >
      <Flex
        css={`
          > * {
            border-bottom: 1px solid ${toColorString(color.opacity.charcoal15)};
          }
          ul li:not(:last-child) {
            border-bottom: 1px solid ${toColorString(color.opacity.charcoal15)};
          }
        `}
        flexDirection="column"
      >
        <Flex flexDirection="column" padding={1}>
          <P> {name} </P>
          <P shade={1} size="small">
            {email}
          </P>
          <Tag
            css={`
              width: fit-content;
              margin-top: ${rem(spacing.base / 2)};
            `}
            text={roleLabel}
            variant="outline"
          />
        </Flex>
        <Menu items={menuItems} />
      </Flex>
    </KDSTopNav.Menu>
  );
}

UserMenu.propTypes = {
  variant: PropTypes.oneOf(['full', 'compact']),
};

UserMenu.defaultProps = {
  variant: 'full',
};

export default UserMenu;
